import { z } from 'zod';
// ============================================================
// Payment Schemas
// ============================================================
export const PaymentTypeSchema = z.enum([
    'PREMIUM_UPGRADE',
    'AI_CREDITS',
    'AI_CREDITS_BUNDLE',
    'INVITATION_PACKAGE',
    'CREDITS_10',
    'CREDITS_20',
    'CREDITS_30',
    'CREDITS_50',
    'CREDITS_100',
    // @deprecated 신규는 INVITATION_PACKAGE 사용. prod 과거 payments 호환 위해 enum 유지.
    'INVITATION_MAIN',
    'STUDIO_PHOTOS_10',
    'STUDIO_PHOTOS_20',
    'STUDIO_PHOTOS_30',
    'SNAP_MAIN',
    'SNAP_PHOTOS_10',
    'SNAP_PHOTOS_20',
    'THANKYOU_ADDON',
    'ALL_PACKAGE',
    'THANKS_PACKAGE',
]);
export const PaymentStatusSchema = z.enum(['PENDING', 'COMPLETED', 'FAILED', 'REFUNDED']);
export const PaymentMethodSchema = z.enum(['TOSS', 'KAKAO_PAY', 'CARD', 'NAVER_PAY']);
export const PaymentChannelSchema = z.enum(['SITE', 'SMARTSTORE']);
export const PaymentSchema = z.object({
    id: z.string().cuid2(),
    userId: z.string().cuid2(),
    type: PaymentTypeSchema,
    method: PaymentMethodSchema,
    amount: z.number().int().min(0),
    creditsGranted: z.number().int().min(0).nullable(),
    status: PaymentStatusSchema.default('PENDING'),
    orderId: z.string().max(255).nullable(),
    paymentKey: z.string().max(255).nullable(),
    createdAt: z.date(),
});
export const CreatePaymentRequestSchema = z.object({
    type: PaymentTypeSchema,
    method: PaymentMethodSchema.default('TOSS'),
});
export const ApprovePaymentRequestSchema = z.object({
    orderId: z.string().min(1, '주문 ID가 필요합니다'),
    paymentKey: z.string().min(1, '결제 키가 필요합니다'),
    amount: z.number().int().min(0, '결제 금액이 필요합니다'),
});
export const PaymentResponseSchema = PaymentSchema.omit({
    userId: true,
    paymentKey: true,
});
export const PaymentListResponseSchema = z.object({
    payments: z.array(PaymentResponseSchema),
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1).max(100),
});
// ============================================================
// Smartstore
// ============================================================
export const ActivateSmartStoreRequestSchema = z.object({
    orderId: z.string().min(1, '주문번호를 입력하세요'),
});
// ============================================================
// Activation Codes
// ============================================================
export const ActivationCodeStatusSchema = z.enum(['AVAILABLE', 'USED', 'EXPIRED']);
export const ActivateCodeRequestSchema = z.object({
    code: z
        .string()
        .regex(/^FIKT-[A-Z2-9]{4}-[A-Z2-9]{4}$/, '올바른 활성화 코드 형식이 아닙니다 (FIKT-XXXX-XXXX)'),
});
// ============================================================
// 토스 결제
// ============================================================
export const TossReadyRequestSchema = z.object({
    productType: PaymentTypeSchema,
});
export const TossConfirmRequestSchema = z.object({
    orderId: z.string().min(1, '주문 ID가 필요합니다'),
    paymentKey: z.string().min(1, '결제 키가 필요합니다'),
    amount: z.number().int().min(1, '결제 금액이 필요합니다'),
});
/**
 * 셀프 환불 가능 여부 — 새 selfRefundable 필드 우선, 없으면 activatesAccount에서 SAFE 파생.
 * product 미발견 또는 둘 다 미지정 → false(환불 차단)가 안전 기본값.
 * (참고: 이 product 축은 grant.ts의 LotPolicy.refundable(크레딧 lot 축)과 다른 개념)
 */
export function getProductSelfRefundable(p) {
    if (!p)
        return false;
    if (typeof p.selfRefundable === 'boolean')
        return p.selfRefundable;
    // 구 row 파생: activatesAccount=true → 환불불가. 둘 다 미지정이면 false(보수적 차단).
    if (typeof p.activatesAccount === 'boolean')
        return !p.activatesAccount;
    return false;
}
/**
 * 활성화 슬롯을 지급하는 상품인지 여부 (범용/청첩장/감사장 중 하나라도 > 0).
 * 슬롯 상품은 환불 가부·사유를 크레딧이 아니라 "슬롯 소비 여부"로 판정해야 하므로,
 * 환불 자격 판정(evaluateRefundEligibility)에서 슬롯 브랜치 진입 조건으로 쓴다.
 * (참고: selfRefundable(환불 차단 축)·LotPolicy.refundable(크레딧 lot 축)과 별개 개념)
 */
export function getProductGrantsSlots(p) {
    if (!p)
        return false;
    return (p.universalSlots ?? 0) + (p.invitationSlots ?? 0) + (p.thankyouSlots ?? 0) > 0;
}
/**
 * 구매 페이지 분류 — 새 category 필드 우선, 없으면 type/activatesAccount에서 파생.
 */
export function getProductCategory(p) {
    if (p.category)
        return p.category;
    // wedding 탭: 청첩장·올인원(activatesAccount) + 감사장 상품
    if (p.activatesAccount)
        return 'wedding';
    if (p.type === 'THANKS_PACKAGE' || p.type === 'THANKYOU_ADDON')
        return 'wedding';
    // studio 탭: AI 스튜디오·스냅
    if (p.type.startsWith('STUDIO_'))
        return 'studio';
    if (p.type.startsWith('SNAP_'))
        return 'studio';
    // 분류 미지정 + 알 수 없는 type(레거시 등)도 구매 페이지에서 사라지지 않도록 studio 탭으로 기본 노출
    return 'studio';
}
/**
 * 구매 페이지 카드 체크 항목(features) — 카탈로그 features 우선, 비면 type별 기본 문구 fallback.
 * 카탈로그 features는 운영자가 적은 문구 그대로, fallback만 credits를 보간한다.
 */
export function getProductFeatures(p) {
    if (p.features && p.features.length > 0)
        return p.features;
    if (p.type === 'THANKS_PACKAGE') {
        return ['감사장 2장 제작 가능', '다양한 디자인 템플릿', '카카오톡·문자로 간편 공유'];
    }
    if (p.type === 'ALL_PACKAGE') {
        return [
            '청첩장 + 감사장 모두 포함',
            `AI 스튜디오 사용권 ${p.credits}개 포함`,
            '워터마크 제거 · 전체 템플릿',
            '참석여부 · 방명록',
        ];
    }
    if (!getProductSelfRefundable(p)) {
        return [
            `AI 스튜디오 사용권 ${p.credits}개 포함`,
            '워터마크 제거',
            '전체 템플릿 사용',
            '참석여부 · 방명록',
        ];
    }
    return [`AI 사진 ${p.credits}장 생성`];
}
// 스마트스토어 7개 옵션과 1:1 매칭. 가격은 스마트스토어 노출가 기준 (2026-05-11 동기화).
// 운영 중 가격 변경 시 admin > 앱 설정의 app_settings.product_catalog 업데이트.
export const DEFAULT_PRODUCT_CATALOG = [
    {
        // 청첩장 패키지 — 스마트스토어 단일상품(sellerProductCode=INVITATION_PACKAGE) & 사이트 청첩장.
        // 활성화 + AI 크레딧 3 + 범용 활성화 슬롯 3 (INVITATION_MAIN과 동일 혜택).
        type: 'INVITATION_PACKAGE',
        nameKo: '모바일 청첩장',
        description: '나만의 모바일 청첩장 제작',
        credits: 3,
        price: 11200,
        basePrice: 16000,
        discountPercent: 30,
        channels: ['SITE', 'SMARTSTORE'],
        activatesAccount: true,
        requiresActivation: false,
        universalSlots: 3,
    },
    {
        type: 'STUDIO_PHOTOS_10',
        nameKo: 'AI 스튜디오 사진 10장',
        credits: 10,
        price: 15000,
        basePrice: 15000,
        discountPercent: 0,
        // SITE(토스) 채널에서 노출 제외 — 스튜디오 상품은 스마트스토어에서만 판매
        channels: ['SMARTSTORE'],
        activatesAccount: false,
        requiresActivation: true,
    },
    {
        type: 'STUDIO_PHOTOS_20',
        nameKo: 'AI 스튜디오 사진 20장',
        credits: 20,
        price: 25000,
        basePrice: 25000,
        discountPercent: 0,
        channels: ['SMARTSTORE'],
        activatesAccount: false,
        requiresActivation: true,
    },
    {
        type: 'STUDIO_PHOTOS_30',
        nameKo: 'AI 스튜디오 사진 30장',
        credits: 30,
        price: 30000,
        basePrice: 30000,
        discountPercent: 0,
        channels: ['SITE', 'SMARTSTORE'],
        activatesAccount: false,
        requiresActivation: true,
    },
    {
        type: 'SNAP_MAIN',
        nameKo: 'AI 스냅 메인 (10장)',
        credits: 10,
        price: 15000,
        basePrice: 20000,
        discountPercent: 25,
        // SITE 노출 제외 — 스냅 메인은 스마트스토어 전용으로 운영
        channels: ['SMARTSTORE'],
        activatesAccount: false,
        requiresActivation: true,
    },
    {
        type: 'SNAP_PHOTOS_10',
        nameKo: 'AI 스냅 10장',
        credits: 10,
        price: 15000,
        basePrice: 15000,
        discountPercent: 0,
        channels: ['SITE', 'SMARTSTORE'],
        activatesAccount: false,
        requiresActivation: true,
    },
    {
        type: 'SNAP_PHOTOS_20',
        nameKo: 'AI 스냅 20장',
        credits: 20,
        price: 25000,
        basePrice: 25000,
        discountPercent: 0,
        channels: ['SITE', 'SMARTSTORE'],
        activatesAccount: false,
        requiresActivation: true,
    },
    {
        // 감사장 상품 — 감사장 활성화 슬롯 2개 부여 (감사장 전용). 생성 한도는 안 올림.
        // 사이트 판매는 THANKS_PACKAGE 하나로 정리 — THANKYOU_ADDON은 기능 중복(슬롯2) placeholder라
        // SITE 채널 제외(스마트스토어 전용으로만 잔존). PurchasePage에는 노출되지 않는다.
        type: 'THANKYOU_ADDON',
        nameKo: '감사장 활성화',
        // 가격은 운영 확정 후 admin app_settings.product_catalog에서 갱신. 아래는 placeholder.
        credits: 0,
        price: 5000,
        basePrice: 5000,
        discountPercent: 0,
        channels: ['SMARTSTORE'],
        activatesAccount: false,
        requiresActivation: true,
        thankyouSlots: 2,
    },
    {
        // 웨딩 올인원 패키지 — 청첩장 전용 활성화 슬롯 3 + 감사장 전용 슬롯 2 + AI 크레딧 20
        type: 'ALL_PACKAGE',
        nameKo: '웨딩 올인원 패키지',
        description: '청첩장 + 감사장 + AI 스튜디오 올인원',
        credits: 20,
        price: 24910,
        basePrice: 47000,
        discountPercent: 47,
        channels: ['SITE', 'SMARTSTORE'],
        activatesAccount: true,
        requiresActivation: false,
        invitationSlots: 3,
        thankyouSlots: 2,
        promoBadge: 'AI 스냅 10장 추가 혜택',
    },
    {
        // 감사장 패키지 — 감사장 활성화 슬롯 2개 (크레딧 없음)
        type: 'THANKS_PACKAGE',
        nameKo: '감사장 패키지',
        description: '감사한 마음을 전하는 모바일 감사장',
        credits: 0,
        price: 7480,
        basePrice: 11000,
        discountPercent: 32,
        channels: ['SITE', 'SMARTSTORE'],
        activatesAccount: false,
        requiresActivation: true,
        thankyouSlots: 2,
    },
];
// ============================================================
// 구매 보너스 크레딧 프로모션
// ============================================================
/** 구매/활성화 시 보너스 크레딧을 지급하는 프로모션 1건. app_settings.credit_promotions 배열에 저장. */
export const CreditPromotionSchema = z.object({
    /** 고유 id — 어드민 UI에서 crypto.randomUUID()로 발급 */
    id: z.string().min(1),
    /** 관리용 이름 (예: "5월 청첩장 런칭 이벤트") */
    name: z.string().min(1),
    /** 대상 상품 타입 — 하나라도 구매 시 매칭 */
    paymentTypes: z.array(PaymentTypeSchema).min(1),
    /** 고정 보너스 크레딧 수량 */
    bonusCredits: z.number().int().min(1),
    /** 프로모션 시작 (ISO datetime) */
    startsAt: z.string().datetime(),
    /** 프로모션 종료 (ISO datetime, 포함) */
    endsAt: z.string().datetime(),
    /** 활성 여부 */
    enabled: z.boolean(),
});
/** app_settings.credit_promotions 전체 값 */
export const CreditPromotionListSchema = z.array(CreditPromotionSchema);
/** 구매/활성화에 적용된 프로모션 1건의 결과 (API 응답·UI 공용) */
export const BonusPromotionSchema = z.object({
    name: z.string(),
    bonusCredits: z.number().int(),
});
// ============================================================
// Constants (deprecated — 기존 코드 호환용)
// ============================================================
/** @deprecated DEFAULT_PRODUCT_CATALOG 사용 */
export const PAYMENT_AMOUNTS = {
    PREMIUM_UPGRADE: 9900,
    AI_CREDITS: 1000,
    AI_CREDITS_BUNDLE: 8000,
};
/** @deprecated DEFAULT_PRODUCT_CATALOG 사용 */
export const CREDITS_GRANTED = {
    PREMIUM_UPGRADE: 10,
    AI_CREDITS: 1,
    AI_CREDITS_BUNDLE: 10,
};
export const validatePaymentAmount = (type, amount) => {
    // 새 상품 타입은 카탈로그에서 검증
    const product = DEFAULT_PRODUCT_CATALOG.find((p) => p.type === type);
    if (product)
        return product.price === amount;
    // 레거시 타입
    const legacy = PAYMENT_AMOUNTS[type];
    return legacy === amount;
};
export const calculateCredits = (type) => {
    // 새 상품 타입은 카탈로그에서 조회
    const product = DEFAULT_PRODUCT_CATALOG.find((p) => p.type === type);
    if (product)
        return product.credits;
    // 레거시 타입
    return CREDITS_GRANTED[type] ?? 0;
};
// ============================================================
// 스마트스토어 자동 활성화
// ============================================================
export const CheckUnactivatedResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        activated: z.array(z.object({
            productOrderId: z.string(),
            productName: z.string(),
            creditsGranted: z.number().int(),
            activationSlotsGranted: z.number().int(),
        })),
    }),
});
export const generateOrderId = (userId, type) => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    return `${type}_${userId.slice(0, 8)}_${timestamp}_${random}`;
};
//# sourceMappingURL=payment.js.map